<template lang="pug">
.ad-page-card
  form.form(@submit.prevent)
    .field
      label.label Active date
      .control
        DateRangePicker(
          ref="picker"
          v-model="dateRange"
          :opens="opens"
          :singleDatePicker="singleDatePicker"
          :timePicker="timePicker"
          :timePicker24Hour="timePicker24Hour"
          :showWeekNumbers="showWeekNumbers"
          :showDropdowns="showDropdowns")
          template(v-slot:input="picker" style="min-width: 350px;") {{ picker.startDate | date }} - {{ picker.endDate | date }}
    .field
      label.label Title
      .control
        input.input(type="text" placeholder="Title" v-model="adData.title")
    .field
      label.label URL
      .control
        input.input(type="text" placeholder="Redirect link" v-model="adData.url")
    .field
      label.label Image link
      .control
        input.input(type="text" placeholder="Banner image link" v-model="adData.imageUrl")
    .field
      label.label Content
      .control
        textarea.textarea(placeholder="Content" v-model="adData.content")
    .level
      .level-left
        .level-item
          .field
            label.label Position
            .control.select
              select.select(v-model="adData.position")
                template(v-for="row of positions")
                  option(:value="row") {{ row }}
      .level-right
        .level-item
          .field
            label.label Status
            .control.select
              select.select(v-model="adData.status")
                template(v-for="row of statuses")
                  option(:value="row") {{ row }}
    .field(:class="{ 'is-grouped': id }")
      .control
        button.button.is-primary(@click="save") Save
      .control
        button.button.is-danger(@click="removeAd" v-if="id") Delete
</template>

<script>
import moment from "moment";
import DateRangePicker from "vue2-daterange-picker";
import "vue2-daterange-picker/dist/vue2-daterange-picker.css";

const ADS_STATUS_ENUM = {
  ACTIVE: "active",
  INACTIVE: "inactive"
};

const ADS_POSITION_ENUM = {
  TOP: "top",
  MIDDLE: "middle",
  BOTTOM: "bottom",
  LEFT: "left",
  RIGHT: "right",
  MOBILE: "mobile",
};

const defaultAd = {
  position: ADS_POSITION_ENUM.TOP,
  status: ADS_STATUS_ENUM.INACTIVE,
  title: "",
  content: "",
  url: "",
  imageUrl: "",
  startDate: new Date(),
  endDate: new Date()
};

const positions = Object.values(ADS_POSITION_ENUM);
const statuses = Object.values(ADS_STATUS_ENUM);

export default {
  name: "Ad",
  components: { DateRangePicker },
  props: {
    position: {
      type: String,
      default: ADS_POSITION_ENUM.TOP,
      validator(value) {
        return positions.includes(value);
      }
    },
    status: {
      type: String,
      default: ADS_STATUS_ENUM.INACTIVE,
      validator(value) {
        return statuses.includes(value);
      }
    },
    title: {
      type: String,
      required: false
    },
    content: {
      type: String,
      required: false
    },
    url: {
      type: String,
      required: false
    },
    imageUrl: {
      type: String,
      required: false
    },
    startDate: {
      type: String,
      required: true,
      default: new Date()
    },
    endDate: {
      type: String,
      required: true,
      default: new Date()
    }
  },
  data() {
    return {
      opens: "right",
      timePicker: false,
      timePicker24Hour: false,
      singleDatePicker: false,
      showWeekNumbers: false,
      showDropdowns: false,
      positions,
      statuses,
      adData: Object.assign({}, defaultAd)
    };
  },
  watch: {
    position() {
      this.adData.position = this.position;
    },
    status() {
      this.adData.status = this.status;
    },
    title() {
      this.adData.title = this.title;
    },
    content() {
      this.adData.content = this.content;
    },
    url() {
      this.adData.url = this.url;
    },
    imageUrl() {
      this.adData.imageUrl = this.imageUrl;
    },
    startDate() {
      this.adData.startDate = this.startDate;
    },
    endDate() {
      this.adData.endDate = this.endDate;
    }
  },
  computed: {
    dateRange: {
      get() {
        return {
          startDate: moment(this.adData.startDate),
          endDate: moment(this.adData.endDate)
        };
      },
      set(value) {
        this.adData.startDate = value.startDate.toISOString();
        this.adData.endDate = value.endDate.toISOString();
      }
    },
    isNew() {
      return this.id === undefined;
    },
    id() {
      return this.$vnode.key;
    }
  },
  filters: {
    date(value) {
      if (!value) return "";

      let options = { year: "numeric", month: "long", day: "numeric" };
      return Intl.DateTimeFormat("en-US", options).format(value);
    }
  },
  methods: {
    // eslint-disable-next-line max-lines-per-function
    save() {
      const data = this.adData;

      if (this.isNew) {
        this.$store
          .dispatch("saveAd", data)
          .then(() => {
            Object.keys(defaultAd).forEach(key => {
              this.adData[key] = defaultAd[key];
            });
          })
          .catch(text => {
            this.$notify({
              type: "error",
              title: "Save Ad error",
              text
            });
          });
      } else {
        this.$store
          .dispatch("updateAd", { _id: this.id, data })
          .then(() => {
            this.$notify({
              type: "success",
              title: "Success",
              text: "Add updated"
            });
          })
          .catch(text => {
            this.$notify({
              type: "error",
              title: "Update Ad error",
              text
            });
          });
      }
    },
    removeAd() {
      if (confirm("Are you sure?")) {
        this.$store.dispatch("removeAd", this.id).catch(text => {
          this.$notify({
            type: "error",
            title: "Remove Ad error",
            text
          });
        });
      }
    }
  },
  mounted() {
    Object.keys(this.$props).forEach(key => {
      this.adData[key] = this.$props[key];
    });
  }
};
</script>

<style lang="scss" scoped>
.ad-page-card {
  padding: 0.5rem;
  border-radius: 5px;
  background-color: #2b353c;

  .form {
    .field {
      label {
        color: #fff;
      }
    }
  }

  .vue-daterange-picker {
    width: 100%;
    & /deep/ .form-control {
      border-radius: 3px;
      color: #000;
      transition: all 0.2s linear;
      &:hover {
        background: rgba(255, 255, 255, 0.5);
      }
    }
    & /deep/ .daterangepicker {
      top: 2rem;
      .calendars {
        display: flex;
        .ranges {
          color: #000;
        }

        table {
          tr {
            color: #333;
          }
        }
      }
    }
  }
}
</style>
